import React, { memo, useEffect, useState, useMemo } from 'react';
import { Card, Col, Row } from 'antd';
import SortableList, { SortableItem } from 'react-easy-sort';
import { arrayMoveImmutable } from 'array-move';

import { BannerProps } from '../service/banners.interface';
import { getSrc } from '../../../../../utils/common/getSrc';
import { BannerLink } from './BannerLink';
import { BannerControls } from './BannerControls';

interface Props {
    readonly banners: BannerProps[];
    readonly updatePosition: (items: BannerProps[]) => void;
    readonly loading: boolean;
}

export const BannersList = memo(
    ({ banners, updatePosition, loading }: Props) => {
        const [items, setItems] = useState<BannerProps[]>([]);

        const memoizedItems = useMemo(() => banners, [banners]);

        useEffect(() => {
            setItems(memoizedItems);
        }, [memoizedItems]);

        const onSortEnd = (oldIndex: number, newIndex: number) => {
            const newItems = arrayMoveImmutable(items, oldIndex, newIndex);
            setItems(newItems);
            void updatePosition(newItems);
        };

        return (
            <SortableList style={{ cursor: 'move' }} onSortEnd={onSortEnd}>
                {items.map(banner => (
                    <SortableItem key={banner.id}>
                        <Card
                            loading={loading}
                            type='inner'
                            style={{ marginBottom: 24 }}
                        >
                            <Row gutter={24}>
                                <Col span={10}>
                                    <div
                                        style={{
                                            width: '100%',
                                            aspectRatio: '3 / 1',
                                            backgroundPosition: 'center',
                                            backgroundRepeat: 'no-repeat',
                                            backgroundSize: 'contain',
                                            backgroundImage: `url(${getSrc(
                                                `/storage/${banner.images?.original}`
                                            )})`,
                                        }}
                                    />
                                </Col>

                                <Col
                                    span={11}
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                    }}
                                >
                                    <BannerLink url={banner.url} />
                                </Col>

                                <Col
                                    span={3}
                                    style={{
                                        display: 'flex',
                                        justifyContent: 'flex-end',
                                    }}
                                >
                                    <BannerControls bannerId={banner.id} />
                                </Col>
                            </Row>
                        </Card>
                    </SortableItem>
                ))}
            </SortableList>
        );
    }
);
